import { useRouterState } from '@tanstack/react-router';
import { cn } from '@/shared/lib/cn';
import {
  sidebarClasses,
  sidebarCollapsedClasses,
  sidebarFooterClasses,
  sidebarIconClasses,
  sidebarItemActiveClasses,
  sidebarItemClasses,
  sidebarItemLabelClasses,
  sidebarItemTwoLineClasses,
  sidebarNavClasses,
} from '@/shared/layout/sidebar/styles';
import type { SidebarProps } from '@/shared/layout/sidebar/types';

/**
 * The icon a destination falls back to when it declares none.
 *
 * A sidebar where some rows carry a mark and others do not reads as broken: the
 * icon column is what the eye scans down, and a gap in it makes the list look
 * half-loaded. Every row therefore gets a glyph, and a destination that has not
 * chosen one gets the neutral gallery placeholder rather than nothing.
 *
 * This used to be a five-entry `id -> name` map, which meant every destination
 * added after it was written silently rendered blank. The fallback belongs to the
 * registry, not to a list that has to be kept in step with the navigation.
 */
const FALLBACK_ICON = 'gallery';

export function useSidebarProps({ items, collapsed }: SidebarProps) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  return {
    rootProps: { className: cn(sidebarClasses, collapsed && sidebarCollapsedClasses) },
    navProps: { className: sidebarNavClasses, 'aria-label': 'Primary' },
    items: items.map((item) => {
      const active = pathname === item.to;
      const twoLine = Boolean(item.description);
      return {
        ...item,
        key: item.to,
        icon: item.icon ?? FALLBACK_ICON,
        linkProps: {
          className: cn(
            sidebarItemClasses,
            twoLine && sidebarItemTwoLineClasses,
            active && sidebarItemActiveClasses,
          ),
          'aria-current': active ? ('page' as const) : undefined,
        },
        iconClasses: cn(
          sidebarIconClasses,
          active ? 'text-accent' : 'text-content-muted group-hover:text-content',
        ),
        labelProps: { className: sidebarItemLabelClasses },
        active,
      };
    }),
    footerProps: { className: sidebarFooterClasses },
  };
}
